export function jaggedHLine(
  context: CanvasRenderingContext2D,
  startFrom: number,
  width: number,
  top: number,
  roughWidth: number,
  roughVariance: number
) {
  // default sections for a jag is 2
  // if the top is longer than the default rough width, increase the section count
  let sections = 2;
  if (width > roughWidth) {
    sections = Math.round(Math.abs(width) / roughWidth);
  }

  let seed = 1;

  const random = () => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  for (let section = 1; section < sections; section++) {
    const x = startFrom + (width * section) / sections;
    const y = top - roughVariance + random() * roughVariance * 2;
    context.lineTo(x, y);
  }

  context.lineTo(startFrom + width, top);
}

export function jaggedVLine(
  context: CanvasRenderingContext2D,
  startFrom: number,
  width: number,
  bottomWidth: number,
  height: number,
  top: number,
  roughWidth: number,
  roughVariance: number
) {
  // default sections for a jag is 2
  // if the top is longer than the default rough width, increase the section count

  let sections = 2;
  if (Math.abs(height) > roughWidth) {
    sections = Math.round(Math.abs(height) / roughWidth);
  }

  let seed = 1;

  const random = () => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  for (let section = 1; section < sections; section++) {
    const x =
      startFrom +
      width +
      (bottomWidth * section) / sections -
      roughVariance +
      random() * roughVariance * 2;
    const y = top + (height * section) / sections;

    context.lineTo(x, y);
  }

  context.lineTo(startFrom + width + bottomWidth, top + height);
}

export function drawShapeBackground(
  context: CanvasRenderingContext2D,
  background: CanvasImageSource,
  diagramEdge: number,
  top: number,
  edgeType: "straight" | "roughEdge" | "roughTop" | "roughAll" | "none",
  currentEdge: number,
  topWidth: number,
  roughWidth: number = 30,
  roughVariance: number = 6,
  bottomWidth: number,
  height: number
): void {
  // reset the context
  context.strokeStyle = "";
  context.lineWidth = 1;
  context.globalAlpha = 1;

  // start the path for the background
  context.beginPath();
  context.moveTo(diagramEdge, top);

  if (edgeType === "straight" || edgeType === "roughEdge") {
    // for a straight or rough edge type, draw the top line as a straight line
    // line goes from diagram edge (usually 0) to current edge + width of this step
    context.lineTo(currentEdge + topWidth, top);
  } else {
    // if the edge is not straight, draw a jagged edge here
    jaggedHLine(context, currentEdge, topWidth, top, roughWidth, roughVariance);
  }

  if (edgeType === "straight" || edgeType === "roughTop") {
    // for a straight or rough top type, draw the edge line as a straight line
    context.lineTo(currentEdge + topWidth + bottomWidth, top + height);
  } else {
    // Rough edge
    jaggedVLine(
      context,
      currentEdge,
      topWidth,
      bottomWidth,
      height,
      top,
      roughWidth,
      roughVariance
    );
  }

  // close the polygon
  context.lineTo(diagramEdge, top + height);
  context.closePath();
  // save the context, set a clip and clear the fill style
  context.save();
  context.clip();
  context.fillStyle = "#fff";

  // Create a pattern with this image, and set it to "repeat".
  const ptrn = context.createPattern(background, "repeat")!;
  context.fillStyle = ptrn;
  context.globalAlpha = 0.5;

  if (diagramEdge > currentEdge) {
    context.fillRect(
      diagramEdge,
      top,
      -(
        Math.abs(diagramEdge) +
        Math.abs(currentEdge) +
        Math.abs(topWidth) +
        Math.abs(bottomWidth)
      ),
      top + height
    );
  } else {
    context.fillRect(
      diagramEdge,
      top,
      Math.abs(diagramEdge) +
        Math.abs(currentEdge) +
        Math.abs(topWidth) +
        Math.abs(bottomWidth),
      top + height
    );
  }

  context.globalAlpha = 1;
  context.restore();
}

export function drawEdges(
  context: CanvasRenderingContext2D,
  diagramEdge: number,
  top: number,
  edgeType: "straight" | "roughEdge" | "roughTop" | "roughAll" | "none",
  currentEdge: number,
  topWidth: number,
  roughWidth: number = 30,
  roughVariance: number = 6,
  bottomWidth: number,
  height: number
) {
  // Draw solid line
  context.lineWidth = 1;
  context.strokeStyle = "#000";

  context.beginPath();
  context.moveTo(currentEdge, top);

  if (edgeType === "straight" || edgeType === "roughEdge") {
    // for a straight or rough edge type, draw the top line as a straight line
    // line goes from diagram edge (usually 0) to current edge + width of this step
    context.lineTo(currentEdge + topWidth, top);
  } else if (edgeType !== "none") {
    // Rough top
    jaggedHLine(context, currentEdge, topWidth, top, roughWidth, roughVariance);
  }

  if (edgeType === "straight" || edgeType === "roughTop") {
    // Straight edge
    context.lineTo(currentEdge + topWidth + bottomWidth, top + height);
  } else if (edgeType !== "none") {
    // Rough edge
    jaggedVLine(
      context,
      currentEdge,
      topWidth,
      bottomWidth,
      height,
      top,
      roughWidth,
      roughVariance
    );
  }

  context.stroke();
}

export function drawPitBottom(
  context: CanvasRenderingContext2D,
  top: number,
  startFrom: number,
  end: number
) {
  // Draw solid line
  context.lineWidth = 1;
  context.strokeStyle = "#000";

  context.beginPath();
  context.moveTo(startFrom, top);
  context.lineTo(end, top);
  context.stroke();
  context.restore();
}

export function drawDivider(
  context: CanvasRenderingContext2D,
  layerLine: "dotted" | "solid" | "none" = "none",
  start: number,
  end: number,
  top: number
) {
  if (layerLine === "dotted") {
    // Draw dotted line
    context.lineWidth = 1;
    context.strokeStyle = "#000";
    context.setLineDash([5, 4]);

    context.beginPath();
    context.moveTo(start, top);
    context.lineTo(end, top);
    context.stroke();
    context.setLineDash([]);
  }
  if (layerLine === "solid") {
    // Draw dotted line
    context.lineWidth = 1;
    context.strokeStyle = "#000";

    context.beginPath();
    context.moveTo(start, top);
    context.lineTo(end, top);
    context.stroke();
  }
}

export function drawMeasurements(
  context: CanvasRenderingContext2D,
  topWidth: number,
  bottomWidth: number,
  currentEdge: number,
  diagramEdge: number,
  top: number,
  height: number,
  holeWidth: number,
  originalEdge: number,
  maxWidthLeft: number,
  maxWidthRight: number,
  currentMeasuredWidth: number = 0,
  currentMeasuredHeight: number = 0,
  hMeasurements:
    | "both"
    | "top"
    | "bottom"
    | "combined"
    | "logTop"
    | "logBottom",
  vMeasurements: "show" | "defer" | "none",
  totalWidth: number,
  totalDepth: number,
  graphWidth: number,
  graphDepth: number
) {
  // Draw measurement lines
  if (topWidth * bottomWidth >= 0) {
    // Both dimensions are in the same direction, ie
    // ((topWidth>=0 && bottomWidth>=0) || (topWidth<=0 && bottomWidth<=0))
    if (
      topWidth !== 0 &&
      (hMeasurements === "both" || hMeasurements === "top")
    ) {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth,
        top,
        Math.abs(topWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }

    if (
      bottomWidth !== 0 &&
      (hMeasurements === "both" || hMeasurements === "bottom")
    ) {
      drawMeasurementLine(
        context,
        currentEdge + topWidth,
        currentEdge + topWidth + bottomWidth,
        top,
        Math.abs(bottomWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (topWidth + bottomWidth !== 0 && hMeasurements === "combined") {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth + bottomWidth,
        top,
        Math.abs(topWidth + bottomWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  } else if (Math.abs(topWidth) > Math.abs(bottomWidth)) {
    if (
      topWidth !== 0 &&
      (hMeasurements === "top" || hMeasurements === "combined")
    ) {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth,
        top,
        Math.abs(topWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (topWidth + bottomWidth !== 0 && hMeasurements === "both") {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth + bottomWidth,
        top,
        Math.abs(topWidth + bottomWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (
      bottomWidth !== 0 &&
      (hMeasurements === "both" || hMeasurements === "bottom")
    ) {
      drawMeasurementLine(
        context,
        currentEdge + topWidth + bottomWidth,
        currentEdge + topWidth,
        top,
        Math.abs(bottomWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  } else {
    if (
      topWidth !== 0 &&
      (hMeasurements === "top" || hMeasurements === "both")
    ) {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth,
        top,
        Math.abs(topWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (topWidth + bottomWidth !== 0 && hMeasurements === "both") {
      drawMeasurementLine(
        context,
        currentEdge,
        currentEdge + topWidth + bottomWidth,
        top,
        Math.abs(bottomWidth + topWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (
      bottomWidth !== 0 &&
      (hMeasurements === "combined" || hMeasurements === "bottom")
    ) {
      drawMeasurementLine(
        context,
        currentEdge + topWidth + bottomWidth,
        currentEdge + topWidth,
        top,
        Math.abs(topWidth + bottomWidth),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  }

  if (currentEdge > diagramEdge) {
    if (
      hMeasurements === "logTop" &&
      ((diagramEdge === 0 && currentEdge + topWidth > currentMeasuredWidth) ||
        (diagramEdge > 0 && currentEdge + topWidth < currentMeasuredWidth))
    ) {
      drawMeasurementLine(
        context,
        currentEdge - currentMeasuredWidth,
        currentEdge + topWidth,
        80,
        Math.abs(topWidth + currentMeasuredWidth).toFixed(0),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (
      hMeasurements === "logBottom" &&
      ((diagramEdge === 0 &&
        currentEdge + topWidth + bottomWidth > currentMeasuredWidth) ||
        (diagramEdge > 0 &&
          currentEdge + topWidth + bottomWidth < currentMeasuredWidth))
    ) {
      drawMeasurementLine(
        context,
        currentEdge - currentMeasuredWidth,
        currentEdge + topWidth + bottomWidth,
        80,
        Math.abs(topWidth + bottomWidth + currentMeasuredWidth).toFixed(0),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  } else {
    if (
      hMeasurements === "logTop" &&
      topWidth !== 0 &&
      ((diagramEdge === 0 && currentEdge + topWidth < currentMeasuredWidth) ||
        (diagramEdge > 0 && currentEdge + topWidth > currentMeasuredWidth))
    ) {
      drawMeasurementLine(
        context,
        currentEdge - currentMeasuredWidth,
        currentEdge + topWidth,
        80,
        Math.abs(topWidth + currentMeasuredWidth).toFixed(0),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
    if (
      hMeasurements === "logBottom" &&
      (topWidth !== 0 || bottomWidth !== 0) &&
      ((diagramEdge === 0 &&
        currentEdge + topWidth + bottomWidth < currentMeasuredWidth) ||
        (diagramEdge > 0 &&
          currentEdge + topWidth + bottomWidth > currentMeasuredWidth))
    ) {
      drawMeasurementLine(
        context,
        currentEdge - currentMeasuredWidth,
        currentEdge + topWidth + bottomWidth,
        80,
        Math.abs(topWidth + bottomWidth + currentMeasuredWidth).toFixed(0),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  }

  if (vMeasurements === "show") {
    let gapSize = 25;
    if (
      holeWidth + originalEdge - maxWidthRight - 60 <
      maxWidthLeft + originalEdge + 45
    ) {
      gapSize = 0; // If less than 60 pixel gap between holes then bring line in a bit
    }
    if (
      holeWidth + originalEdge - maxWidthRight - 30 <=
      maxWidthLeft + originalEdge
    ) {
      gapSize = -35; // If less than 60 pixel gap between holes then bring line in a bit
    }
    if (diagramEdge === 0) {
      // left hole details
      drawMeasurementVertical(
        context,
        top - currentMeasuredHeight,
        top + height,
        maxWidthLeft + originalEdge + gapSize + 35,
        Math.abs(currentMeasuredHeight + height),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    } else {
      drawMeasurementVertical(
        context,
        top - currentMeasuredHeight,
        top + height,
        originalEdge - maxWidthRight - 18,
        Math.abs(currentMeasuredHeight + height),
        totalWidth,
        totalDepth,
        graphWidth,
        graphDepth
      );
    }
  }
  if (vMeasurements === "defer") {
    currentMeasuredHeight += height;
  } else {
    currentMeasuredHeight = 0;
  }
}

export function drawMeasurementLine(
  context: CanvasRenderingContext2D,
  leftEdge: number,
  rightEdge: number,
  topEdge: number,
  content: string | number,
  totalWidth: number,
  totalDepth: number,
  graphWidth: number,
  graphDepth: number
) {
  const scaledLeftEdge = scale(leftEdge, totalWidth, graphWidth);
  const scaledRightEdge = scale(rightEdge, totalWidth, graphWidth);
  const scaledTopEdge = scale(topEdge, totalDepth, graphDepth);
  const scaled25 = scale(25, totalDepth, graphDepth);
  const scaled20 = scale(20, totalDepth, graphDepth);
  const scaled10 = scale(10, totalDepth, graphDepth);
  const scaled5 = scale(5, totalDepth, graphDepth);

  context.lineWidth = 1;
  context.strokeStyle = "#000";

  context.beginPath();
  context.moveTo(
    scaledLeftEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) + scaled10
  );
  context.lineTo(
    scaledLeftEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) - scaled10
  );

  context.moveTo(
    scaledLeftEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20)
  );
  context.lineTo(
    scaledRightEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20)
  );

  context.moveTo(
    scaledRightEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) - scaled10
  );
  context.lineTo(
    scaledRightEdge,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) + scaled10
  );

  context.moveTo(
    scaledLeftEdge + scaled5,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) - scaled5
  );
  context.lineTo(
    scaledLeftEdge - scaled5,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) + scaled5
  );

  context.moveTo(
    scaledRightEdge + scaled5,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) - scaled5
  );
  context.lineTo(
    scaledRightEdge - scaled5,
    scaledTopEdge + (rightEdge < leftEdge ? scaled20 : -scaled20) + scaled5
  );

  context.stroke();

  context.font = "12px Arial";
  context.textAlign = "center";
  context.textBaseline = "bottom";
  context.strokeStyle = "#ffffff";
  context.shadowColor = "rgba(255,255,255,0.6)";
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 15;
  context.lineWidth = 4;

  context.strokeText(
    `${content}`,
    (scaledRightEdge + scaledLeftEdge) / 2,
    scaledTopEdge + (rightEdge < leftEdge ? scaled25 : -scaled25)
  );
  context.fillStyle = "#000000";
  context.fillText(
    `${content}`,
    (scaledRightEdge + scaledLeftEdge) / 2,
    scaledTopEdge + (rightEdge < leftEdge ? scaled25 : -scaled25)
  );
}

export function drawMeasurementVertical(
  context: CanvasRenderingContext2D,
  topEdge: number,
  bottomEdge: number,
  leftEdge: number,
  content: string | number,
  totalWidth: number,
  totalDepth: number,
  graphWidth: number,
  graphDepth: number
) {
  const scaledLeftEdge = scale(leftEdge, totalWidth, graphWidth);
  const scaledTopEdge = scale(topEdge, totalDepth, graphDepth);
  const scaledBottomEdge = scale(bottomEdge, totalDepth, graphDepth);
  const scaled30 = scale(30, totalDepth, graphDepth);
  const scaled25 = scale(25, totalDepth, graphDepth);
  const scaled20 = scale(20, totalDepth, graphDepth);
  const scaled15 = scale(15, totalDepth, graphDepth);
  const scaled10 = scale(10, totalDepth, graphDepth);
  const scaled5 = scale(5, totalDepth, graphDepth);

  context.lineWidth = 1;
  context.strokeStyle = "#000";

  context.beginPath();
  context.moveTo(scaledLeftEdge + scaled10, scaledTopEdge);
  context.lineTo(scaledLeftEdge + scaled30, scaledTopEdge);

  context.moveTo(scaledLeftEdge + scaled20, scaledTopEdge);
  context.lineTo(scaledLeftEdge + scaled20, scaledBottomEdge);

  context.moveTo(scaledLeftEdge + scaled30, scaledBottomEdge);
  context.lineTo(scaledLeftEdge + scaled10, scaledBottomEdge);

  context.moveTo(scaledLeftEdge + scaled25, scaledTopEdge + scaled5);
  context.lineTo(scaledLeftEdge + scaled15, scaledTopEdge - scaled5);

  context.moveTo(scaledLeftEdge + scaled25, scaledBottomEdge + scaled5);
  context.lineTo(scaledLeftEdge + scaled15, scaledBottomEdge - scaled5);

  context.stroke();

  context.font = "12px Arial";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.strokeStyle = "#ffffff";
  context.lineWidth = 4;

  context.save();
  context.translate(
    scaledLeftEdge + scaled25,
    (scaledTopEdge + scaledBottomEdge) / 2
  );
  context.rotate(-Math.PI / 2);
  context.strokeText(`${content}`, 0, -15);
  context.fillStyle = "#000000";
  context.fillText(`${content}`, 0, -15);
  context.restore();
}

export function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const cars = text.split("\n");

  y -= (lineHeight * (cars.length - 1)) / 2;

  for (const car of cars) {
    let line = "";
    const words = car.split(" ");

    for (const word of words) {
      const testLine = line + word + " ";
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth) {
        context.strokeText(line, x, y);
        context.fillText(line, x, y);
        line = word + " ";
        y += lineHeight;
      } else {
        line = testLine;
      }
    }

    context.strokeText(line, x, y);
    context.fillText(line, x, y);
    y += lineHeight;
  }
}

export function drawText(
  context: CanvasRenderingContext2D,
  text: string,
  diagramEdge: number,
  currentEdge: number,
  topWidth: number,
  top: number,
  height: number
) {
  // Draw text
  context.font = "14px Arial";
  context.textAlign = "center";
  context.textBaseline = "middle";

  context.shadowColor = "rgba(255,255,255,0.8)";
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 15;

  context.strokeStyle = "#ffffff";
  context.lineWidth = 4;

  context.fillStyle = "#000000";

  if (text && text.substring(0, 2) !== "##") {
    wrapText(
      context,
      text,
      (diagramEdge + currentEdge + topWidth) / 2,
      top + height / 2,
      Math.abs(diagramEdge - currentEdge) - 10,
      36
    );
  }
  context.shadowBlur = 0;
}

export function scale(
  value: number,
  relativeTo: number,
  graphDimension: number
) {
  return value * (graphDimension / relativeTo);
}

export function drawFeature(
  context: CanvasRenderingContext2D,
  feature: string,
  label: string,
  labelJustification: CanvasTextAlign,
  labelRotation: number,
  position: string,
  shape: string,
  leftEdge1: number,
  leftEdge2: number,
  topEdge1: number,
  topEdge2: number,
  originalEdge: number,
  originalTop: number,
  depth: number,
  width: number,
  height: number,
  pitWidth: number,
  graphWidth: number
) {
  leftEdge1 = leftEdge1 + originalEdge;
  leftEdge2 = leftEdge2 + originalEdge;
  topEdge1 = topEdge1 + originalTop;
  topEdge2 = topEdge2 + originalTop;
  depth = depth + originalTop;

  context.textAlign = labelJustification;
  switch (feature) {
    case "pipe":
      // Parallel to wall
      if (position === "parallel") {
        // Calculate length of translation
        const screenIntervals =
          Math.round(
            Math.sqrt(
              (leftEdge1 - leftEdge2) * (leftEdge1 - leftEdge2) +
                (topEdge1 - topEdge2) * (topEdge1 - topEdge2)
            )
          ) / 5;
        for (let i = 1; i < screenIntervals; i++) {
          context.beginPath();
          context.arc(
            leftEdge1 + ((leftEdge2 - leftEdge1) / screenIntervals) * i,
            topEdge1 +
              width / 2 +
              ((topEdge2 - topEdge1) / screenIntervals) * i,
            width / 2,
            0,
            2 * Math.PI
          );
          context.strokeStyle =
            "rgb(" +
            Math.round(200 - (200 / screenIntervals) * i) +
            "," +
            Math.round(200 - (200 / screenIntervals) * i) +
            "," +
            Math.round(200 - (200 / screenIntervals) * i) +
            ")";

          context.lineWidth = 1;
          context.stroke();
        }
        for (let i = 1; i < screenIntervals; i++) {
          context.beginPath();
          context.arc(
            leftEdge1 + ((leftEdge2 - leftEdge1) / screenIntervals) * i,
            topEdge1 +
              width / 2 +
              ((topEdge2 - topEdge1) / screenIntervals) * i,
            width / 2 - 2,
            0,
            2 * Math.PI
          );
          context.fillStyle = "#fff";
          context.fill();
        }

        context.beginPath();
        context.arc(leftEdge1, topEdge1 + width / 2, width / 2, 0, 2 * Math.PI);
        context.strokeStyle = "rgb(200,200,200)";
        context.fillStyle = "#fff";
        // context.stroke();
        // context.fill();

        context.beginPath();
        context.arc(leftEdge2, topEdge2 + width / 2, width / 2, 0, 2 * Math.PI);
        context.strokeStyle = "#000";
        context.fillStyle = "#fff";
        context.lineWidth = 1;
        context.stroke();
        context.fill();
      } else if (position === "adjacent") {
        context.beginPath();
        context.moveTo(leftEdge1, topEdge1);
        context.lineTo(leftEdge2, topEdge2);
        context.lineTo(leftEdge2, topEdge2 + width);
        context.lineTo(leftEdge1, topEdge1 + width);
        context.strokeStyle = "#333";
        context.lineWidth = 1;
        context.stroke();
      }

      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      if (label.substring(0, 2) !== "##") {
        context.save();
        if (labelJustification === "left") {
          context.translate(
            leftEdge1 + (leftEdge2 - leftEdge1) + 5,
            topEdge1 + (topEdge2 + width - topEdge1) / 2
          );
        } else if (labelJustification === "right") {
          context.translate(
            leftEdge1 - 5,
            topEdge1 + (topEdge2 + width - topEdge1) / 2
          );
        } else {
          context.translate(
            leftEdge1 + (leftEdge2 - leftEdge1) / 2,
            topEdge1 + (topEdge2 + width - topEdge1) / 2
          );
        }
        context.rotate((-labelRotation * Math.PI) / 180);
        context.strokeText(label, 0, 0);
        context.fillStyle = "#000000";
        context.fillText(label, 0, 0);
        context.restore();
      }

      break;
    case "object":
      if (shape === "circle") {
        context.beginPath();
        context.arc(
          leftEdge1 + width / 2,
          depth + width / 2,
          width / 2,
          0,
          2 * Math.PI
        );
        context.strokeStyle = "#000";
        context.lineWidth = 1;
        context.stroke();
      } else if (shape === "rectangle") {
        context.beginPath();
        context.rect(leftEdge1, depth, width, height);
        context.strokeStyle = "#000";
        context.lineWidth = 1;
        context.stroke();
      }

      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      if (label.substring(0, 2) !== "##") {
        context.save();
        if (labelJustification === "left") {
          context.translate(leftEdge1 + width + 5, depth + height / 2);
        } else if (labelJustification === "right") {
          context.translate(leftEdge1 - 5, depth + height / 2);
        } else {
          context.translate(leftEdge1 + width / 2, depth + height / 2);
        }
        context.rotate((-labelRotation * Math.PI) / 180);
        context.strokeText(label, 0, 0);
        context.fillStyle = "#000000";
        context.fillText(label, 0, 0);
        context.restore();

        // context.strokeText(label,scale(leftEdge1+(width)/2),scale(depth+(height/2)));
        // context.fillStyle = '#000000';
        // context.fillText(label,scale(leftEdge1+(width)/2),scale(depth+(height/2)));
      }
      break;
    case "hLine":
      context.beginPath();
      context.moveTo(leftEdge1, topEdge1);
      context.lineTo(leftEdge1 + width, topEdge1);
      context.strokeStyle = "#000";
      context.lineWidth = 2;
      context.stroke();

      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      if (label.substring(0, 2) !== "##") {
        context.save();
        context.translate(leftEdge1 + width / 2, topEdge1 - 10);
        context.rotate((-labelRotation * Math.PI) / 180);
        context.strokeText(label, 0, 0);
        context.fillStyle = "#000000";
        context.fillText(label, 0, 0);
        context.restore();

        // context.strokeText(label,scale(leftEdge1+(width)/2),scale(topEdge1));
        // context.fillStyle = '#000000';
        // context.fillText(label,scale(leftEdge1+(width)/2),scale(topEdge1));
      }
      break;
    case "vLine":
      context.beginPath();
      context.moveTo(leftEdge1, topEdge1);
      context.lineTo(leftEdge1, topEdge1 + height);
      context.strokeStyle = "#000";
      context.lineWidth = 2;
      context.stroke();

      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      if (label.substring(0, 2) !== "##") {
        context.save();
        context.translate(leftEdge1 + 10, topEdge1 + height / 2);
        context.rotate((-labelRotation * Math.PI) / 180);
        context.strokeText(label, 0, 0);
        context.fillStyle = "#000000";
        context.fillText(label, 0, 0);
        context.restore();

        // context.strokeText(label,scale(leftEdge1),scale(topEdge1+(height/2)));
        // context.fillStyle = '#000000';
        // context.fillText(label,scale(leftEdge1),scale(topEdge1+(height/2)));
      }
      break;
    case "waterLevel":
      context.beginPath();
      context.moveTo(0, depth);

      for (let i = 0; i < graphWidth; i += 5) {
        context.lineTo(i, depth + Math.sin(i / 10) * 5);
      }
      context.lineTo(graphWidth, depth);
      context.strokeStyle = "#33f";
      context.lineWidth = 1;
      context.stroke();

      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      if (label.substring(0, 2) !== "##") {
        context.save();
        context.translate((originalEdge + pitWidth) / 2, depth);
        context.rotate((-labelRotation * Math.PI) / 180);
        context.strokeText(label, 0, 0);
        context.fillStyle = "#000000";
        context.fillText(label, 0, 0);
        context.restore();

        // context.strokeText(label,scale((originalEdge+holeWidth/2)),scale(depth));
        // context.fillStyle = '#000000';
        // context.fillText(label,scale((originalEdge+holeWidth/2)),scale(depth));
      }

      break;
    case "label":
      // Draw text
      context.font = "14px Arial";
      context.textBaseline = "middle";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      context.save();
      context.translate(leftEdge1, depth);
      context.rotate((-labelRotation * Math.PI) / 180);
      context.strokeText(label, 0, 0);
      context.fillStyle = "#000000";
      context.fillText(label, 0, 0);
      context.restore();

      // context.strokeText(label,scale(leftEdge1),scale(depth));
      // context.fillStyle = '#000000';
      // context.fillText(label,scale(leftEdge1),scale(depth));
      break;
  }
}

export function drawSample(
  context: CanvasRenderingContext2D,
  sampleType: string,
  leftEdge: number,
  depth: number,
  testResult: string
) {
  const measurementText = testResult.substr(0, testResult.lastIndexOf("\n"));
  const resultText = testResult.substr(testResult.lastIndexOf("\n"));
  const lines = testResult.split("\n").length;

  switch (sampleType) {
    case "dm":
      // Draw text
      context.font = "14px Arial";
      context.textAlign = "left";
      context.textBaseline = "top";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      context.strokeText("DM", leftEdge, depth);
      context.fillStyle = "#000000";
      context.fillText("DM", leftEdge, depth);

      if (measurementText.length > 0) {
        wrapText(
          context,
          measurementText,
          leftEdge + 40,
          depth + ((lines - 2) * 17) / 2,
          100,
          17
        );
      } // Draw all except last line on the right hand side
      if (resultText.length > 0) {
        wrapText(
          context,
          resultText,
          leftEdge,
          depth + (lines - 2) * 17 - 17 / 2,
          100,
          17
        );
      } // Draw all except last line on the right hand side

      // draw horizontal line
      context.beginPath();
      context.moveTo(leftEdge, depth + (lines - 1) * 17);
      context.lineTo(leftEdge + 60, depth + (lines - 1) * 17);
      context.strokeStyle = "#000";
      context.lineWidth = 1;
      context.stroke();

      break;
    case "dv":
      // Draw text
      context.font = "14px Arial";
      context.textAlign = "left";
      context.textBaseline = "top";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      context.strokeText("DV", leftEdge, depth);
      context.fillStyle = "#000000";
      context.fillText("DV", leftEdge, depth);

      if (testResult.length > 0) {
        wrapText(
          context,
          testResult,
          leftEdge + 30,
          depth + ((lines - 1) * 17) / 2,
          100,
          17
        );
      } // Draw all except last line on the right hand side

      break;
    default:
      const displayTest =
        sampleType.length > 0
          ? sampleType.substr(sampleType.length - 2, 1)
          : "";

      // Draw text
      context.font = "14px Arial";
      context.textAlign = "center";
      context.textBaseline = "bottom";

      context.shadowColor = "rgba(255,255,255,0.8)";
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.shadowBlur = 15;

      context.strokeStyle = "#ffffff";
      context.lineWidth = 4;

      context.strokeText(displayTest, leftEdge, depth);
      context.fillStyle = "#000000";
      context.fillText(displayTest, leftEdge, depth);
      if (testResult.length > 0) {
        wrapText(context, testResult, leftEdge + 30, depth, 100, 17);
      }
      break;
  }
}
